import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LeapV2Student } from '../models/leap-v2-student.model';
import {
  AttendanceStatus,
  MODAL_STATUSES,
  StudentAttendance,
  AttendanceDialogData,
  AttendanceDialogResult,
} from '../models/leap-v2-attendance.model';
import { LeapV2AttendanceDialogComponent } from '../dialogs/leap-v2-attendance-dialog/leap-v2-attendance-dialog.component';

interface AttendanceOption {
  label: string;
  value: AttendanceStatus;
  cssClass: string;
}

@Component({
  selector: 'leap-v2-attendance-card',
  templateUrl: './leap-v2-attendance-card.component.html',
  styleUrls: ['./leap-v2-attendance-card.component.css'],
  standalone: false,
})
export class LeapV2AttendanceCardComponent implements OnChanges {
  @Input() student: LeapV2Student;
  @Input() disabled = false;
  /**
   * The student's already-persisted attendance (from the loaded session), if any. Seeds the saved-state
   * view — the status pill and the reason/comments/parent-carer panel — so a re-opened or post-submit
   * session shows exactly what was captured, not the empty status buttons. Null when nothing is saved yet.
   */
  @Input() existingAttendance: StudentAttendance | null = null;
  @Output() attendanceSaved = new EventEmitter<StudentAttendance>();
  @Output() attendanceCleared = new EventEmitter<string>();

  savedAttendance: StudentAttendance | null = null;

  // Mirror the persisted attendance into the local saved-state whenever it changes (initial load and each
  // reload). session.attendance is the single source of truth and only changes through this card's own
  // save/clear, so this stays in sync rather than clobbering an in-progress edit.
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['existingAttendance']) {
      this.savedAttendance = this.existingAttendance;
    }
  }

  readonly options: AttendanceOption[] = [
    { label: 'Present', value: 'Present', cssClass: 'tw:!text-[#2e7d32] tw:!bg-[#e8f5e9]' },
    { label: 'Late', value: 'Late', cssClass: 'tw:!text-[#f57f17] tw:!bg-[#fff8e1]' },
    { label: 'Cancelled - 1+ days notice', value: 'Cancelled - 1+ days notice', cssClass: 'tw:!text-[#e65100] tw:!bg-[#fff3e0]' },
    { label: 'Cancelled - same day', value: 'Cancelled - same day', cssClass: 'tw:!text-[#e65100]  tw:!bg-[#fff3e0]' },
    { label: 'Absent', value: 'Absent', cssClass: 'tw:!text-white tw:!bg-[#c00]' },
  ];

  get pillClass(): string {
    if (!this.savedAttendance) return '';
    switch (this.savedAttendance.status) {
      case 'Present': return 'tw:bg-[#2e7d32] tw:text-white tw:border-[#2e7d32]';
      case 'Late':    return 'tw:bg-[#f57f17] tw:text-white tw:border-[#f57f17]';
      case 'Absent':  return 'tw:bg-[#a00] tw:text-white tw:border-[#a00]';
      default:        return 'tw:bg-[#e65100] tw:text-white tw:border-[#e65100]';
    }
  }

  get pillLabel(): string {
    if (!this.savedAttendance) return '';
    return this.options.find(o => o.value === this.savedAttendance.status)?.label ?? this.savedAttendance.status;
  }

  constructor(private dialog: MatDialog) {}

  selectAttendance(status: AttendanceStatus): void {
    if (this.disabled) return;

    if (MODAL_STATUSES.includes(status)) {
      const data: AttendanceDialogData = { studentName: this.student.name, status };
      this.dialog.open(LeapV2AttendanceDialogComponent, { width: '480px', data })
        .afterClosed()
        .subscribe((result: AttendanceDialogResult) => {
          if (result) {
            this.save(status, result.reason, result.comments, result.parentCarerInformed);
          }
        });
    } else {
      this.save(status, undefined, '', undefined);
    }
  }

  changeAttendance(): void {
    this.savedAttendance = null;
    this.attendanceCleared.emit(this.student.id);
  }

  private save(status: AttendanceStatus, reason?: string, comments?: string, parentCarerInformed?: string): void {
    this.savedAttendance = { studentId: this.student.id, status, reason, comments, parentCarerInformed };
    this.attendanceSaved.emit(this.savedAttendance);
  }
}
